
import { approvePeriodApi, createSalaryApi, deleteSalaryeApi, getSalariesApi, makePeriodApi, updateSalaryApi } from '@app/api/employee/salary.api';
import { SalaryDataArray, SalaryItem, SalaryModel, SalaryQueryParams, SalaryTotals } from '@app/interfaces/salary';
import { AnyAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { message } from 'antd';
import { showError } from '@app/utils/message';
import moment from 'moment';

export interface SalaryState {
  data: SalaryModel[];
  totalRows: number;
  /** Quyền xem bảo hiểm phần công ty do backend trả; null = chưa tải danh sách. */
  canViewCompanyInsurance: boolean | null;
  totals: SalaryTotals | null;
  pagination: {
    current: number,
    pageSize: number
  },
  filters: any,
  sorter: any,
  searchTerm: string;
  selectedRecord: SalaryItem[];
  loading: boolean;
  error: string | null;
}

const initialState: SalaryState = {
  data: [],
  totalRows: 0,
  canViewCompanyInsurance: null,
  totals: null,
  pagination: { current: 1, pageSize: 20 },
  filters: {
    from_date: moment().startOf('month').format('YYYY-MM-DD'),
    to_date: moment().endOf('month').format('YYYY-MM-DD'),
  },
  sorter: {},
  searchTerm: '',
  selectedRecord: [],
  loading: false,
  error: null,
};

interface ResponseData {
  data: SalaryItem;
  message: string;
}

export const getSalaries = createAsyncThunk<SalaryDataArray, SalaryQueryParams>(
  'salary/getSalaries',
  async (params: SalaryQueryParams, { rejectWithValue }) => {
    try {
      const customers = (await getSalariesApi(params)) as SalaryDataArray;
      return customers || [];
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
    }
  },
);

export const createSalary = createAsyncThunk<ResponseData, SalaryModel>(
  'salary/createSalary', async (data) => {
  const response = await createSalaryApi(data);
  return response;
});

export const updateSalary = createAsyncThunk<ResponseData, { employeeId: number; formData: SalaryModel }>(
  'salary/updateSalary',
  async ({ employeeId, formData }) => {
    const response = await updateSalaryApi(employeeId, formData);
    return response;
  },
);

export const deleteSalary = createAsyncThunk<{ employeeId: number; response: any }, number>(
  'salary/deleteSalary',
  async (employeeId, { rejectWithValue }) => {
    try {
      const response = await deleteSalaryeApi(employeeId);
      return { employeeId, response };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
    }
  },
);

export const makePeriod = createAsyncThunk<ResponseData, SalaryModel>(
  'salary/makePeriod', async (data) => {
  const response = await makePeriodApi(data);
  return response;
});

export const approvePeriod = createAsyncThunk<string, any>(
  'salary/approvePeriod', async (data) => {
  const response = await approvePeriodApi(data);
  return response;
});

const isRejectedAction = (action: AnyAction) => {
  return action.type.endsWith('rejected');
};

// Slice
export const salarySlice = createSlice({
  name: 'salary',
  initialState,
  reducers: {
    updateTableParams(state, action) {
      const { pagination, filters, sorter, searchTerm } = action.payload;
      state.pagination = pagination;
      state.filters = filters;
      state.sorter = sorter;
      state.searchTerm = searchTerm;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSalaries.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSalaries.fulfilled, (state, action) => {
        state.loading = false;

        const data = action.payload.rows;
        state.data = data.map((item: SalaryItem) => item.attributes) || [];

        state.totalRows = parseInt(action.payload.total_rows);
        state.canViewCompanyInsurance = action.payload.can_view_company_insurance ?? null;
        state.totals = action.payload.totals ?? null;
      })
      .addCase(getSalaries.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch salary';
      })
      .addCase(createSalary.fulfilled, (state, action) => {
        const { data, message: newMessage } = action.payload;
        state.data.unshift(data.attributes);
        message.success(newMessage);
      })
      .addCase(updateSalary.fulfilled, (state, action) => {
        const { data, message: newMessage } = action.payload;

        const updatedIndex = state.data.findIndex((item) => item.id === data.id);
        if (updatedIndex !== -1) {
          state.data[updatedIndex] = data.attributes;
          message.success(newMessage);
        }
      })
      .addCase(deleteSalary.fulfilled, (state, action) => {
        const { employeeId, response } = action.payload;
        state.data = state.data.filter((product) => Number(product.id) !== employeeId);
        message.success(response.message);
      })
      // Không chèn tay vào `data`: kỳ mới có thể nằm ngoài bộ lọc kỳ lương đang xem
      // và phải theo đúng sort/phân trang. SalaryHeader tải lại danh sách sau khi tạo.
      .addCase(makePeriod.fulfilled, (_state, action) => {
        message.success((action.payload as any).message);
      })
      .addCase(approvePeriod.fulfilled, (state, action) => {
        message.success((action.payload as any).message);
      })
      .addMatcher(isRejectedAction, (state, action) => {
        state.loading = false;
        showError(action.payload || action.error.message);
      });
  },
});

export const { updateTableParams } = salarySlice.actions;

export default salarySlice.reducer;
